// Per-token identifier quoting helpers (v2 Slice H2 / DSE2-8). Picks the
// quoting char used by the source identifier and applies it consistently
// when synthesizing matching new tokens (e.g. new column names that should
// match the source table's quoting style — `[Foo]` vs `"Foo"` vs unquoted).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice H2.
//
// Resolution order for `inferQuoting(qn, engine)`:
//   1. `qn.quoting` (set by `readQualifiedName` from the leaf part's quote
//      char).
//   2. First char of `qn.raw` (`[` → bracket, `"` → double-quote, `` ` `` →
//      backtick) when raw is non-empty.
//   3. `qn.quoted` boolean × engine default (matches v1 `maybeQuoteIdent`).
//
// Synthesizers call `quoteAsOwner(engine, name, owner)` to wrap a new name
// in the same style the owner used. The "unquoted" case returns the bare
// identifier — case folding is the engine's job at execute time, not ours.

import type { IdentifierQuoting, QualifiedName, SqlEngine } from '../../../sql-grammar/model';

import { isReservedWord } from './dialect.reservedWords';

export { isReservedWord };

export const ENGINE_DEFAULT_QUOTING: Record<SqlEngine, IdentifierQuoting> = {
  mssql: 'bracket',
  postgres: 'double-quote',
  mysql: 'backtick',
  oracle: 'double-quote',
  sqlite: 'double-quote',
};

/** Wrap `name` in the chosen quoting style. `'unquoted'` returns the bare
 *  identifier. Used by synthesizers when emitting new tokens that should
 *  match an owner's source style. */
export function quoteWithStyle(name: string, quoting: IdentifierQuoting): string {
  switch (quoting) {
    case 'bracket': return `[${name.replace(/]/g, ']]')}]`;
    case 'double-quote': return `"${name.replace(/"/g, '""')}"`;
    case 'backtick': return `\`${name.replace(/`/g, '``')}\``;
    case 'unquoted':
    default: return name;
  }
}

/** Infer the quoting style of an existing identifier. Prefers the explicit
 *  `qn.quoting` field; falls back to inspecting `qn.raw[0]`; falls back to
 *  the engine default × `qn.quoted` boolean (v1 posture). */
export function inferQuoting(qn: QualifiedName, engine: SqlEngine): IdentifierQuoting {
  if (qn.quoting !== undefined) return qn.quoting;
  if (qn.raw && qn.raw.length > 0) {
    const inferred = inferQuotingFromRaw(qn.raw, engine);
    if (inferred !== null) return inferred;
  }
  if (qn.quoted) return ENGINE_DEFAULT_QUOTING[engine];
  return 'unquoted';
}

/** Inspect the first char of a raw identifier source slice (which may
 *  include a `database.schema.name` chain). Returns the matching quoting
 *  style, or null if the char is not a recognized open-quote for the
 *  engine. */
export function inferQuotingFromRaw(raw: string, engine: SqlEngine): IdentifierQuoting | null {
  const c = raw[0];
  if (c === '[' && (engine === 'mssql' || engine === 'sqlite')) return 'bracket';
  if (c === '`' && (engine === 'mysql' || engine === 'sqlite')) return 'backtick';
  if (c === '"' && engine !== 'mysql') return 'double-quote';
  if (/[A-Za-z_]/.test(c ?? '')) return 'unquoted';
  return null;
}

/** Wrap `name` in the owner's quoting style — the v2 Slice H2 entry point.
 *  This is the per-token preservation primitive: when a synthesizer emits a
 *  new identifier alongside an existing owner (e.g. `ALTER TABLE [Foo] ADD
 *  COLUMN [Bar]`), the new token should match the owner's style char. */
export function quoteAsOwner(engine: SqlEngine, name: string, owner: QualifiedName): string {
  return quoteWithStyle(name, inferQuoting(owner, engine));
}

/** The characters each engine accepts in a BARE identifier, first one
 *  included. `_` and `$` are common to all five after the first character;
 *  `#` is a regular-identifier character on SQL Server and Oracle only —
 *  PostgreSQL, MySQL and SQLite reject an unquoted `user#id` at parse time.
 *  A leading digit is refused everywhere: MySQL does allow one, but
 *  over-delimiting a name is always valid SQL and under-delimiting is not.
 *
 *  Oracle is the one engine that also refuses a leading `_`, measured: `_ab`
 *  bare is ORA-00911, so a catalog `_tmp` there can only have been created
 *  delimited and naming it bare is a parse error — the same class as `#`. The
 *  other four accept `_` in first position.
 *
 *  What this table is NOT is Oracle's accepted alphabet. It is the
 *  conservative EMISSION policy — "may I safely write this without
 *  delimiters" — and it is ASCII-only on purpose. Oracle accepts a good deal
 *  more bare than this admits (`①ab` stores as `①AB`; a leading Thai vowel
 *  sign or combining mark is accepted), which is the TOKEN question and has
 *  its own table, {@link BARE_TOKEN}. Conflating the two was a defect this
 *  file carried for several rounds. */
const BARE_IDENTIFIER: Record<SqlEngine, RegExp> = {
  mssql: /^[A-Za-z_][A-Za-z0-9_$#]*$/,
  oracle: /^[A-Za-z][A-Za-z0-9_$#]*$/,
  postgres: /^[A-Za-z_][A-Za-z0-9_$]*$/,
  mysql: /^[A-Za-z_][A-Za-z0-9_$]*$/,
  sqlite: /^[A-Za-z_][A-Za-z0-9_$]*$/,
};

/** What each engine actually ACCEPTS undelimited. This is a DIFFERENT question
 *  from {@link BARE_IDENTIFIER}, and conflating the two was a defect this file
 *  carried: that table is the conservative WRITING policy ("may I safely emit
 *  this without delimiters"), deliberately ASCII-only so an emitter delimits
 *  anything it is unsure of. Asking it "was this token bare?" gets the wrong
 *  answer for a name the server is perfectly happy to read bare.
 *
 *  Each row is that engine's own lexer rule, not one shared guess — the
 *  servers disagree about the high range and the disagreements are measured:
 *
 *    postgres  ANY byte >= 0x80, in any position — format, separator and
 *              control characters included. Bare `Straße`, `ﬁle`, `a²`,
 *              `a½`, `Col€`, `x→y`, a combining mark, `A\u200Cx` and a leading
 *              `\uFEFF` are all accepted, and folded ASCII-only (`Col€` →
 *              `col€`, `A\u200Cx` → `a\u200Cx`). Narrowing this to exclude
 *              format characters was a DEFECT: a name the server folded came
 *              back unfolded, so the emitted spelling named nothing.
 *    sqlite    the same, with ONE exception measured at the lexer: a LEADING
 *              U+FEFF is skipped as whitespace (`(\uFEFFAx integer)` stores
 *              `Ax`), while an inner one is kept. It folds nothing.
 *    mysql     U+0080–U+FFFF only (its identifier range stops below the
 *              astral planes), and a digit MAY lead.
 *    oracle    three measured rules rather than one category, and the LEAD
 *              class is measured too — not "a letter", which is the classic
 *              ASCII-era rule: `①ab` stores as `①AB`, and a leading Thai vowel
 *              sign or combining mark is accepted. See
 *              {@link ORACLE_BARE_TOKEN} and {@link ORACLE_LEAD}.
 *    mssql     a letter or `_` leads; `@` and `#` prefixes are excluded here
 *              deliberately, being temp/variable sigils rather than ordinary
 *              column spellings.
 *
 *  Being too WIDE is the dangerous direction: it folds a name the server kept
 *  verbatim, and the emitted spelling can then name a DIFFERENT column. Being
 *  too narrow leaves the bytes alone, which is only ever a loud failure. Both
 *  have happened here, which is why every line above is a measurement. */
/** Oracle's row, and the SHAPE is the finding: no single category rule
 *  survives the measurements, so this is three rules, each matching how a
 *  block actually measured.
 *
 *  1. ALLOW by category where the category measured uniformly — `\p{L}`
 *     (Unicode 15.0; see the drift note on {@link foldSimpleUpper}), `\p{Nd}`,
 *     `\p{Nl}`, and marks.
 *  2. DENY by RANGE where a block measured uniformly refused: the Devanagari
 *     dependent signs U+0901–0903 and U+093C–094D, and the Hebrew points
 *     U+05B0–05C4 — minus U+05BA, which is accepted (measured).
 *  3. INVERT the default for `\p{No}`: 65 of a 105-point SAMPLE are refused
 *     (`²`, `½`, `¹`, `⁰`, `⒈`, `⓪`, `⅓`, `❶`, Tamil `௰`…), so `No` is denied
 *     and only the measured-accepted circled digits U+2460–2487 are allowed.
 *
 *  Plus a per-point list for the `Lm` / `Lu` scatter, which follows no block.
 *
 *  Both directions have bitten this branch. Excluding `\p{Lm}` as a class left
 *  `Abー` — everyday katakana — unfolded; admitting `\p{N}` to let `①` through
 *  re-admitted `²`, whose refusal had been measured and pinned two rounds
 *  earlier. So each line below is a measurement, and the pins name the code
 *  points rather than the categories. */
const ORACLE_REFUSED_POINTS = [
  '\\u02B9', '\\u02BA', '\\u02C6', '\\u02C8', '\\u02CC',  // Lm scatter
  '\\u02CD', '\\u02CF', '\\u02D0', '\\u207F',
  '\\u212B', '\\u2126',                                    // composed Å / Ω
  '\\u093D',                                               // avagraha (Lo)
];
const ORACLE_REFUSED_RANGES = [
  '\\u0901-\\u0903', '\\u093C-\\u094D',   // Devanagari dependent signs
  '\\u05B0-\\u05B9', '\\u05BB-\\u05C4',   // Hebrew points, minus U+05BA
];
// One lookahead PER item rather than one class holding them all: a class
// containing combining marks reads as a single glyph in source, which
// `no-misleading-character-class` objects to (rightly).
const ORACLE_NOT = [...ORACLE_REFUSED_POINTS, ...ORACLE_REFUSED_RANGES]
  .map((r) => `(?:(?![${r}]))`)
  .join('');
/** The `No` block Oracle accepts bare. Measured over a 105-point sample:
 *  U+2460–2487 accepted, every sampled point outside it refused. Denying the
 *  rest is therefore an implementation POLICY chosen from that sample, not an
 *  exhaustive result — and it is the safe direction, since a wrongly denied
 *  point only leaves the bytes alone. */
const ORACLE_ALLOWED_NO = '\\u2460-\\u2487';
const ORACLE_CHAR = `(?:\\p{L}|\\p{M}|\\p{Nd}|\\p{Nl}|[_$#${ORACLE_ALLOWED_NO}])`;
/** What may LEAD. Measured, and it is NOT "a letter": `①ab` stores as `①AB`,
 *  a leading Thai vowel sign (U+0E31) and a leading combining mark (U+0300)
 *  are both accepted. What is refused as a lead and accepted inside is exactly
 *  `_`, `$`, `#` and DIGITS (`1ab` is ORA-03054, `$ab` / `#ab` / `_ab` and a
 *  leading Devanagari digit are ORA-00911) — so the lead class is the
 *  continuation class minus those. */
const ORACLE_LEAD = `(?:\\p{L}|\\p{M}|\\p{Nl}|[${ORACLE_ALLOWED_NO}])`;
const ORACLE_BARE_TOKEN = new RegExp(
  `^${ORACLE_NOT}${ORACLE_LEAD}(?:${ORACLE_NOT}${ORACLE_CHAR})*$`,
  'u',
);

/** SQLite skips a LEADING U+FEFF as whitespace — measured: `CREATE TABLE t
 *  (\uFEFFAx integer)` stores `Ax`, the BOM gone, while an INNER one is kept
 *  (`A\uFEFFx` stores all three characters). So the exclusion belongs on the
 *  first position only, and only here: PostgreSQL accepts a leading U+FEFF as
 *  part of the name and folds it, so excluding it there would have left the
 *  name unfolded and named a spelling the catalog does not hold. */
const SQLITE_LEAD_NOT = '(?!\\uFEFF)';

const BARE_TOKEN: Record<SqlEngine, RegExp> = {
  mssql: /^[\p{L}_][\p{L}\p{M}\p{Nd}\p{Nl}_$#]*$/u,
  oracle: ORACLE_BARE_TOKEN,
  // PostgreSQL's lexer takes ANY byte >= 0x80 in an identifier, format and
  // separator characters included — measured: bare `A\u200Cx` is stored
  // `a\u200Cx` and bare `A\uFEFFx` is accepted. Narrowing this was a defect:
  // a name the server folded came back unfolded.
  postgres: /^[A-Za-z_\u{80}-\u{10FFFF}][A-Za-z0-9_$\u{80}-\u{10FFFF}]*$/u,
  mysql: /^[A-Za-z0-9_$\u{80}-\u{FFFF}]+$/u,
  sqlite: new RegExp(
    `^${SQLITE_LEAD_NOT}[A-Za-z_\\u{80}-\\u{10FFFF}][A-Za-z0-9_$\\u{80}-\\u{10FFFF}]*$`,
    'u',
  ),
};

/** Whether `engine` would read `name` as a bare identifier — the TOKEN
 *  question, not the writing policy. Shape only: it says nothing about
 *  reserved words, which is {@link needsQuoting}'s other half and a caller's
 *  own concern.
 *
 *  Exported for a caller deciding whether a typed string is a column reference
 *  AT ALL rather than how to write one: `a DESC` and `LOWER(email)` fail here,
 *  which is what separates an index entry that names a column from one that is
 *  grammar. */
export function isBareIdentifierShape(name: string, engine: SqlEngine): boolean {
  return BARE_TOKEN[engine].test(name);
}

/** Whether a name cannot stand unquoted ON `engine`. TWO reasons, and a name
 *  hit by either has no bare spelling at all:
 *
 *    1. SHAPE — anything outside that engine's bare-identifier class, or a
 *       leading digit (`my table`, `1st`, `user#id` on PostgreSQL, `_tmp` on
 *       Oracle).
 *    2. The word is RESERVED (`dialect.reservedWords.ts`). `select` has plain
 *       identifier shape and is still not readable as one: PostgreSQL answers
 *       `syntax error at or near "select"` and Oracle `ORA-03050: invalid
 *       identifier: "SELECT" is a reserved word`. Which words those are differs
 *       per engine, which is why the question is asked OF the engine and the
 *       lists are not a union — over-delimiting is NOT free on the two engines
 *       whose delimiters suppress the fold (see that file's header).
 *
 *  Both reasons land in one predicate on purpose: every caller is asking "may
 *  I write this name without delimiters here", and the consequence of a wrong
 *  answer is the same statement-level rejection either way.
 *
 *  `engine` is optional only because two call sites outside the rename paths
 *  (`dialect.tableLifecycle.ts`, `tableLifecycleEdit.ts`) still ask the
 *  engine-independent question; omitting it keeps the historical permissive,
 *  keyword-BLIND class (`#` and a leading `_` allowed, `select` bare) so their
 *  output is unchanged. Pass the engine wherever the answer decides whether an
 *  emitted statement parses. */
export function needsQuoting(name: string, engine?: SqlEngine): boolean {
  if (engine === undefined) return !BARE_IDENTIFIER.mssql.test(name);
  return !BARE_IDENTIFIER[engine].test(name) || isReservedWord(name, engine);
}

/** How the server STORES an identifier a statement spelled WITHOUT
 *  delimiters. Oracle upper-folds, PostgreSQL lower-folds; MySQL, SQLite and
 *  MSSQL keep the typed case.
 *
 *  The complement of {@link needsQuoting}: that one answers "may this spelling
 *  be written bare at all", this one "what survives if it is". Both halves are
 *  needed to decide whether a statement can name a spelling the catalog
 *  ALREADY holds, which is why they live together — `dialect.ts`'s column
 *  ALTER and `rename/terminalTokenEdit.ts`'s catalog-identity prediction read
 *  the SAME table, and a second copy of it is exactly how the two drifted
 *  apart before.
 *
 *  Deliberately NOT `scope/identifiers.ts`'s `foldIdentifier`: that one's
 *  mssql / mysql / sqlite case is the CASE-INSENSITIVE MATCHING fold
 *  (lowercase), a different question from how the server stores the name. */
/** The 27 Greek letters with ypogegrammeni, whose SIMPLE uppercase is the
 *  titlecase form while their FULL uppercase expands to two code points. The
 *  "keep anything that expands" test gets these wrong on its own: measured,
 *  Oracle stores bare `ᾳx` as `ᾼX` (U+1FBC), not as `ᾳX`. */
const SIMPLE_UPPER_EXCEPTION: ReadonlyMap<number, number> = new Map([
  // U+1F80–1F87 → U+1F88–1F8F, U+1F90–1F97 → U+1F98–1F9F,
  // U+1FA0–1FA7 → U+1FA8–1FAF (each +8).
  ...Array.from({ length: 8 }, (_, i) => [0x1f80 + i, 0x1f88 + i] as const),
  ...Array.from({ length: 8 }, (_, i) => [0x1f90 + i, 0x1f98 + i] as const),
  ...Array.from({ length: 8 }, (_, i) => [0x1fa0 + i, 0x1fa8 + i] as const),
  [0x1fb3, 0x1fbc],
  [0x1fc3, 0x1fcc],
  [0x1ff3, 0x1ffc],
]);

/** Oracle's own fold: SIMPLE (1:1) uppercase, which is what the server
 *  applies and what JS `toUpperCase()` does not — it applies FULL mapping and
 *  expands some characters. Measured: bare `straße` is stored `STRAßE` and
 *  `ﬁle` as `ﬁLE`, where `toUpperCase()` gives `STRASSE` and `FILE`.
 *
 *  Two rules, because neither alone is right. A character whose uppercase
 *  expands is normally kept as it is — that is the `ß` / `ﬁ` case. But some
 *  characters HAVE a simple uppercase even though their full one expands, and
 *  keeping those unfolded is equally wrong; {@link SIMPLE_UPPER_EXCEPTION}
 *  carries the measured family.
 *
 *  A caveat worth knowing rather than hiding: this reads the RUNTIME's Unicode
 *  tables, and a server on an older Unicode version folds an exotic letter
 *  differently (Node 22 uppercases `ƛ` U+019B, a pair added in Unicode 16;
 *  Oracle 26ai keeps it). The consequence is ORA-00904 on a name almost
 *  nobody has, never a wrong column — an emitted name that does not resolve
 *  fails loudly. */
function foldSimpleUpper(name: string): string {
  let out = '';
  for (const ch of name) {
    const exception = SIMPLE_UPPER_EXCEPTION.get(ch.codePointAt(0) ?? -1);
    if (exception !== undefined) {
      out += String.fromCodePoint(exception);
      continue;
    }
    const up = ch.toUpperCase();
    out += [...up].length === 1 ? up : ch;
  }
  return out;
}

export function foldAsStored(name: string, engine: SqlEngine): string {
  switch (engine) {
    case 'oracle':
      // Oracle upper-folds every letter, but with SIMPLE (1:1) case mapping,
      // where JS `toUpperCase()` applies FULL mapping and expands some
      // characters. Measured: bare `straße` is stored `STRAßE` and `ﬁle` is
      // stored `ﬁLE`, while `toUpperCase()` gives `STRASSE` and `FILE` —
      // spellings the catalog does not hold, which is the dangerous direction
      // for a fold. So fold per code point and keep any character whose
      // uppercase is not a single code point. (`äbc` → `ÄBC` either way.)
      return foldSimpleUpper(name);
    case 'postgres':
      // PostgreSQL folds ASCII ONLY. Its own documentation calls the fold
      // lower-casing, but the server leaves a non-ASCII letter alone in a
      // multibyte encoding — a bare `Äbc` is stored `Äbc`, not `äbc`
      // (measured). `toLowerCase()` would claim a spelling the catalog does
      // not hold. Most readers never see the difference because
      // `BARE_IDENTIFIER` rejects a non-ASCII name upstream and they delimit
      // it instead; a reader that folds first (the ambiguity copy) does.
      return name.replace(/[A-Z]/g, (c) => c.toLowerCase());
    default:
      return name;
  }
}

/** WHICH COLUMN a BARE token of `name` names on `engine` — the third read of
 *  this table, and the one a caller holding a SCRIPT's own spelling is asking.
 *
 *  {@link needsQuoting} says whether a spelling may be written bare,
 *  {@link foldAsStored} what the server keeps when it is; this one says what it
 *  DENOTES, which is only a different question because DELIMITERS SUPPRESS THE
 *  FOLD. A consumer that renders such a spelling bare never has to ask — the
 *  server folds it — but one that has to ADD delimiters (a quoted host's style,
 *  or a name with no bare form) changes the identity unless it delimits this
 *  answer instead of the typed bytes: `"Target_Col"` is not the column
 *  PostgreSQL's `Target_Col` resolves to.
 *
 *  A spelling with NO bare form on this engine (`user id`, `_tmp` on Oracle,
 *  `user#id` off SQL Server) was never a bare token, so it never folded and its
 *  own bytes are the identity — the shape test below, not `needsQuoting`.
 *
 *  A RESERVED word deliberately DOES fold here, and that is the point: which
 *  words a server reserves moves with its version (PostgreSQL reserved
 *  SYSTEM_USER in 16, Oracle EXCEPT in 21c), while the fold does not. A script
 *  written against PostgreSQL 15 holds a bare `System_User`; on every server,
 *  old or new, that token names `system_user`, so delimiting THAT is correct
 *  without knowing the version. Reading `isReservedWord` here instead would
 *  make the answer version-dependent again — the exact gap this closes. */
export function bareReferenceIdentity(name: string, engine: SqlEngine): string {
  // The TOKEN class, not the writing policy: this asks what the SERVER did
  // with the bytes, and the server folds every name it accepted bare — a
  // non-ASCII one included. Reading the ASCII-only `BARE_IDENTIFIER` here
  // returned `Imię` unfolded, so an emitted `"Imię"` named a spelling
  // PostgreSQL does not hold (it stored `imię`).
  if (!BARE_TOKEN[engine].test(name)) return name;
  return foldAsStored(name, engine);
}

/** Whether naming THIS EXACT spelling on `engine` requires delimiters —
 *  either it has no bare form at all (shape OR reserved word), or the engine's
 *  fold would change its bytes on the way in.
 *
 *  The keyword half is the one a fold test alone cannot reach: `select` IS
 *  PostgreSQL's fold of `select` and `SELECT` IS Oracle's, so the second
 *  clause below says "no delimiters needed" for a column the parser will not
 *  read bare at all — which is how a rename's UNDO came to emit the invalid
 *  `ALTER TABLE users RENAME COLUMN select TO id`.
 *
 *  The second half is what a style flag cannot answer. `quoteNames` mirrors
 *  the source's quoting (DSE6), so a BARE host relation says "unquoted style"
 *  — but an existing catalog column `Id` has no unquoted spelling that still
 *  denotes it: `ALTER TABLE users RENAME COLUMN Id …` asks PostgreSQL for
 *  `id` and Oracle for `ID`, columns the server does not have. Use this
 *  wherever the name is a spelling the catalog already holds; use plain
 *  {@link needsQuoting} where it is a name being CREATED and the fold is the
 *  intended identity. */
export function needsDelimitersToNameExactly(name: string, engine: SqlEngine): boolean {
  return needsQuoting(name, engine) || foldAsStored(name, engine) !== name;
}

/** WHERE a spelling of an ALREADY-EXISTING column came from. It settles the one
 *  question a synthesizer cannot ask the string itself: is this engine's fold
 *  the thing that RESOLVES this name, or the thing that CORRUPTS it?
 *
 *    • `bare-reference` — written WITHOUT delimiters (a script's own token; the
 *      default, and what a caller holding only a name is under). The server
 *      folds such a token to find the column, so when an emitter has to ADD
 *      delimiters — for style, or because the spelling has no bare form on this
 *      engine — they go around the FOLD ({@link bareReferenceIdentity}), never
 *      around the typed bytes.
 *    • `delimited-source` — the SOURCE STATEMENT wrote the name delimited.
 *      Delimiters suppress the fold, so those bytes ARE what the catalog holds:
 *      `ALTER TABLE "users" ADD COLUMN "Id" integer` creates `Id`, and folding
 *      it names `id` — a column that does not exist, or a different sibling
 *      that does.
 *    • `catalog-spelling` — the bytes came from the CATALOG itself (the Object
 *      Explorer tree, via `go-to/columnAtCursor.ts`). The same conclusion as
 *      `delimited-source`, reached from the other end.
 *
 *  The last two deliberately agree: `"Id"` in a source and `Id` in the catalog
 *  are the same fact. They stay separate arms because their CALL SITES cannot
 *  be collapsed — one reads a parsed statement, the other a catalog — and a
 *  parser-side call site forced to claim "this is the catalog's spelling" about
 *  a token it read out of a script is exactly how this third case went missing
 *  when the contract had only two arms. */
export type ExistingNameOrigin = 'bare-reference' | 'delimited-source' | 'catalog-spelling';

/** How to write a name the catalog ALREADY holds: the spelling to emit, and
 *  whether it must carry delimiters. The two halves travel together because
 *  under `bare-reference` the FOLD ONLY APPLIES WHEN DELIMITERS DO — a token
 *  emitted bare is folded by the server itself, and rewriting it would churn
 *  bytes the round-trip corpus pins.
 *
 *  `styleDelimits` is the DSE6 STYLE preference (the host identifier's own
 *  quoting). A style may ADD delimiters; it may never move the name to another
 *  column, which is the whole reason the text is chosen per origin instead of
 *  being the caller's string. */
export function nameExistingColumn(
  name: string,
  engine: SqlEngine,
  origin: ExistingNameOrigin,
  styleDelimits: boolean,
): { readonly text: string; readonly delimit: boolean } {
  if (origin === 'bare-reference') {
    const delimit = styleDelimits || needsQuoting(name, engine);
    return { text: delimit ? bareReferenceIdentity(name, engine) : name, delimit };
  }
  // `delimited-source` / `catalog-spelling`: these bytes are the server's own,
  // so there is no fold to preserve — only the question of whether this engine
  // can be made to READ them, which `needsDelimitersToNameExactly` answers.
  return {
    text: name,
    delimit: styleDelimits || needsDelimitersToNameExactly(name, engine),
  };
}

/** An identifier the user TYPED, split into the bytes it denotes and the
 *  delimiters they wrapped it in. */
export type DecodedIdentifier = {
  /** The identifier's own bytes — delimiters removed, doubled escapes
   *  collapsed. */
  readonly name: string;
  /** The delimiters the user typed; `'unquoted'` when they typed none. */
  readonly quoting: IdentifierQuoting;
};

/** The delimiter pair each quoting style writes. */
const DELIMITERS: Record<Exclude<IdentifierQuoting, 'unquoted'>, readonly [string, string]> = {
  bracket: ['[', ']'],
  'double-quote': ['"', '"'],
  backtick: ['`', '`'],
};

/**
 * Decode an identifier a USER typed, delimiters and all.
 *
 * Free-text entry points (the rename box, the Object Explorer's rename input)
 * accept `"MixedCase"` / `[MixedCase]` / `` `MixedCase` `` just as readily as a
 * bare name, and every surface downstream then has to answer the same question:
 * WHAT IDENTITY does this denote? They only agree if the delimiters come off
 * exactly once, with the fact that the user meant them kept as the returned
 * `quoting` intent — which the synthesizer re-applies, once. Without that,
 * `maybeQuoteIdent` wrapped the typed delimiters a second time
 * (`TO """MixedCase"""` — a column whose NAME contains quote characters) while
 * the body and tab rewrites took the typed spelling verbatim.
 *
 * Decoding is deliberately conservative — anything it cannot read as ONE
 * delimited identifier comes back verbatim and unquoted, which is the historical
 * treatment:
 *   - a delimiter the ENGINE does not accept (`[x]` on PostgreSQL, `"x"` on
 *     MySQL, whose ANSI_QUOTES-off default this codebase assumes),
 *   - an unterminated spelling (`"MixedCase`),
 *   - an inner text that closes the delimiter early (`"a"."b"` is a CHAIN, not
 *     one name; decoding it would invent `a"."b`).
 */
export function decodeDelimitedIdentifier(typed: string, engine: SqlEngine): DecodedIdentifier {
  const style = delimiterStyleOf(typed, engine);
  if (!style) return { name: typed, quoting: 'unquoted' };
  const [open, close] = DELIMITERS[style];
  const inner = typed.slice(open.length, typed.length - close.length);
  if (!escapedThroughout(inner, close)) return { name: typed, quoting: 'unquoted' };
  return { name: inner.split(close + close).join(close), quoting: style };
}

/** The style `typed` opens AND closes with, or `null` when it is not a single
 *  delimited spelling this engine would read that way. */
function delimiterStyleOf(
  typed: string,
  engine: SqlEngine,
): Exclude<IdentifierQuoting, 'unquoted'> | null {
  if (typed.length < 2) return null;
  const first = typed[0];
  const last = typed[typed.length - 1];
  if (first === '[' && last === ']' && (engine === 'mssql' || engine === 'sqlite')) return 'bracket';
  if (first === '`' && last === '`' && (engine === 'mysql' || engine === 'sqlite')) return 'backtick';
  if (first === '"' && last === '"' && engine !== 'mysql') return 'double-quote';
  return null;
}

/**
 * A column reference the CALLER TYPED, split into the identity it names and
 * what its spelling says about where that identity came from.
 *
 * The VQE constraint / index forms take a comma-separated column list out of a
 * free-text input (`parseColumnList` trims and splits, keeping whatever the
 * user typed), and `"Id"` / `[Id]` / `` `Id` `` typed there is not decoration:
 * it is the user saying the bytes ARE the catalog's, which on a FOLDING engine
 * is the only spelling that can name a column called `Id` when the source
 * statement says nothing about it. Left encoded, the delimiters are read as
 * part of the NAME — {@link needsQuoting} fires on their shape and
 * `maybeQuoteIdent` wraps them a second time, emitting
 * `PRIMARY KEY ("""Id""")`: a column whose name contains quote characters. So
 * they come off exactly once here, the same rule the rename entry points
 * follow ({@link decodeDelimitedIdentifier}), and what they asserted survives
 * as `'catalog-spelling'` — the origin for bytes the server already holds,
 * reached from the user's end instead of the parser's.
 *
 * `origin` is `undefined` when the user typed NO delimiters. That spelling
 * asserts nothing, so the caller asks the SOURCE STATEMENT next
 * (`columnEdit.alterTable.ts`'s `sourceSpellingOrigin`) and falls back to
 * `'bare-reference'` — a genuinely bare `System_User` must still fold.
 *
 * A delimiter THIS ENGINE does not read (`[Id]` on PostgreSQL) is not decoded,
 * per {@link decodeDelimitedIdentifier}'s conservative contract: the spelling
 * comes back verbatim with no origin, which is the historical treatment.
 */
export function decodeTypedColumnRef(
  typed: string,
  engine: SqlEngine,
): { readonly name: string; readonly origin?: ExistingNameOrigin } {
  const { name, quoting } = decodeDelimitedIdentifier(typed, engine);
  if (quoting === 'unquoted') return { name };
  return { name, origin: 'catalog-spelling' };
}

/** True when every `close` char inside a delimited body is doubled — the only
 *  way a close delimiter may appear there. */
function escapedThroughout(inner: string, close: string): boolean {
  let i = 0;
  while (i < inner.length) {
    if (inner[i] !== close) { i += 1; continue; }
    if (inner[i + 1] !== close) return false;
    i += 2;
  }
  return true;
}
